import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {CommonModule} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatRadioModule} from '@angular/material/radio';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatIconModule} from '@angular/material/icon';
import {concat, finalize, take} from 'rxjs';
import {MatTooltipModule} from "@angular/material/tooltip";
import {DestinationType} from "../../destinations.types";
import {RestFactoryService} from "../../../../../core/services/rest-factory.service";

@Component({
    selector: 'clone-destination-dialog',
    templateUrl: './clone-destination-dialog.component.html',
    styleUrls: ['./clone-destination-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatRadioModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatTooltipModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CloneDestinationDialogComponent implements OnInit {
    private API_CLONE_DESTINATION = "/admin/destination/clone";
    private API_VERIFY_GOOGLE_ADDRESS = "/admin/destination/checklocation";

    cloneForm: FormGroup;
    isCloning: boolean = false;
    verifying: boolean[] = [];
    verifiedData: any[] = [];

    constructor(
        public dialogRef: MatDialogRef<CloneDestinationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { destination: DestinationType },
        private _fb: FormBuilder,
        private _restFactoryService: RestFactoryService,
        private _changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.cloneForm = this._fb.group({
            count: [1, [Validators.required, Validators.min(1)]],
            type: ['simple', Validators.required],
            clones: this._fb.array([])
        });
        this.updateClonesArray();
    }

    get clones(): FormArray {
        return this.cloneForm.get('clones') as FormArray;
    }

    updateClonesArray(): void {
        const count = this.cloneForm.get('count').value || 1;
        while (this.clones.length !== count) {
            if (this.clones.length < count) {
                this.clones.push(this._fb.group({
                    name: [this.data.destination.ds_name + ` (Clone ${this.clones.length + 1})`, Validators.required],
                    address: [this.data.destination.ds_googleaddress]
                }));
            } else {
                this.clones.removeAt(this.clones.length - 1);
            }
        }
        this.verifying = new Array(count).fill(false);
        this.verifiedData = new Array(count).fill(null);
    }

    verifyAddress(index: number): void {
        const addressControl = this.clones.at(index).get('address');
        if (!addressControl.value) return;

        this.verifying[index] = true;
        this.verifiedData[index] = null;
        const payload = new FormData();
        payload.append('ds_address', addressControl.value);

        this._restFactoryService.httpPostService(this.API_VERIFY_GOOGLE_ADDRESS, payload)
            .pipe(finalize(() => {
                this.verifying[index] = false;
                this._changeDetectorRef.markForCheck();
            }))
            .subscribe(response => {
                if (response.vi_status === 0 && response.destination) {
                    this.verifiedData[index] = response.destination;
                    addressControl.setValue(response.destination.formatted_address);
                }
            });
    }

    executeClone(): void {
        if (this.cloneForm.invalid) return;
        this.isCloning = true;

        const { count, type, clones } = this.cloneForm.value;
        const observables = [];

        if (type === 'simple') {
            for (let i = 0; i < count; i++) {
                const payload = new FormData();
                payload.append("id_destination", this.data.destination.id_destination);
                payload.append("ds_name", `${this.data.destination.ds_name} (Clone ${i + 1})`);
                payload.append("ds_address", this.data.destination.ds_googleaddress);
                observables.push(this._restFactoryService.httpPostService(this.API_CLONE_DESTINATION, payload));
            }
        } else {
            for (let i = 0; i < clones.length; i++) {
                const cloneData = clones[i];
                const payload = new FormData();
                payload.append("id_destination", this.data.destination.id_destination);
                payload.append("ds_name", cloneData.name);
                payload.append("ds_address", cloneData.address);

                const verified = this.verifiedData[i];
                if (verified) {
                    payload.append('vr_lat', verified.location_lat);
                    payload.append('vr_long', verified.location_long);
                }
                observables.push(this._restFactoryService.httpPostService(this.API_CLONE_DESTINATION, payload));
            }
        }

        concat(...observables.map(obs => obs.pipe(take(1)))).subscribe({
            complete: () => {
                this.isCloning = false;
                this.dialogRef.close({ success: true });
            },
            error: () => {
                this.isCloning = false;
            }
        });
    }

    onNoClick(): void {
        this.dialogRef.close();
    }
}
